import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetchEncryptedBlob, apiFetchWorkspaceBlob } from "../lib/authApi";
import { readChatImageCached, writeChatImageCached } from "../lib/chatImageDiskCache";
import {
  b64ToU8,
  decryptImageBytes,
  isE2eVoiceAttachment,
  isWorkspaceServerVoiceAttachment,
  type ChatVoiceAttachmentMeta,
} from "../lib/crypto";

type MessageVoiceBodyProps = {
  attachment: ChatVoiceAttachmentMeta;
  fetchE2eEncryptedBlob?: (blobId: string) => Promise<ArrayBuffer>;
};

function voiceCacheKey(attachment: ChatVoiceAttachmentMeta, vault: boolean): string {
  if (isWorkspaceServerVoiceAttachment(attachment)) {
    return `wsv:${attachment.workspace_id}:${attachment.blob_id}`;
  }
  return vault ? `vaultvoice:${attachment.blob_id}` : `voice:${attachment.blob_id}`;
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const BAR_COUNT = 40;

/** Resample waveform to exactly BAR_COUNT bars. */
function resampleWaveform(raw: number[], bars: number): number[] {
  if (!raw || raw.length === 0) return Array(bars).fill(0.15);
  if (raw.length === bars) return raw;
  const result: number[] = [];
  const step = raw.length / bars;
  for (let i = 0; i < bars; i++) {
    const from = Math.floor(i * step);
    const to = Math.min(Math.ceil((i + 1) * step), raw.length);
    let max = 0;
    for (let j = from; j < to; j++) {
      if ((raw[j] ?? 0) > max) max = raw[j] ?? 0;
    }
    result.push(max);
  }
  const peak = Math.max(...result, 0.01);
  return result.map((v) => Math.max(0.06, v / peak));
}

export function MessageVoiceBody({ attachment, fetchE2eEncryptedBlob }: MessageVoiceBodyProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0–1
  const [currentMs, setCurrentMs] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const animFrameRef = useRef<number | null>(null);

  const cacheKey = voiceCacheKey(attachment, Boolean(fetchE2eEncryptedBlob));
  const waveform = resampleWaveform(attachment.waveform ?? [], BAR_COUNT);
  const durationMs = attachment.duration_ms ?? 0;

  useEffect(() => {
    let cancelled = false;
    setAudioUrl(null);
    setError(null);
    setPlaying(false);
    setProgress(0);
    setCurrentMs(0);

    // Сбрасываем audio элемент при смене вложения, иначе старый продолжит играть
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current.onended = null;
      audioRef.current.onpause = null;
      audioRef.current.onplay = null;
      audioRef.current = null;
    }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }

    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }

    void (async () => {
      try {
        const cached = await readChatImageCached(cacheKey);
        if (cancelled || !cached) return;
        const url = URL.createObjectURL(new Blob([new Uint8Array(cached)], { type: attachment.mime }));
        if (cancelled) { URL.revokeObjectURL(url); return; }
        urlRef.current = url;
        setAudioUrl(url);
      } catch { /* ignore */ }
    })();

    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
      if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, [cacheKey, attachment.mime]);

  const fetchAndLoad = useCallback(async () => {
    if (urlRef.current) return urlRef.current;
    setLoading(true);
    setError(null);
    try {
      let plain: Uint8Array;
      if (isWorkspaceServerVoiceAttachment(attachment)) {
        const buf = await apiFetchWorkspaceBlob(attachment.workspace_id, attachment.blob_id);
        plain = new Uint8Array(buf);
      } else if (isE2eVoiceAttachment(attachment)) {
        const buf = fetchE2eEncryptedBlob
          ? await fetchE2eEncryptedBlob(attachment.blob_id)
          : await apiFetchEncryptedBlob(attachment.blob_id);
        const key = b64ToU8(attachment.key_b64);
        const nonce = b64ToU8(attachment.nonce_b64);
        plain = new Uint8Array(decryptImageBytes(new Uint8Array(buf), nonce, key));
      } else {
        throw new Error("unknown voice attachment type");
      }
      void writeChatImageCached(cacheKey, attachment.mime, plain);
      const url = URL.createObjectURL(new Blob([new Uint8Array(plain)], { type: attachment.mime }));
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setAudioUrl(url);
      return url;
    } catch {
      setError("не удалось загрузить");
      return null;
    } finally {
      setLoading(false);
    }
  }, [attachment, cacheKey, fetchE2eEncryptedBlob]);

  const startProgressLoop = useCallback(() => {
    const tick = () => {
      const audio = audioRef.current;
      if (!audio) return;
      const dur = audio.duration;
      if (dur && isFinite(dur)) {
        setProgress(audio.currentTime / dur);
        setCurrentMs(Math.floor(audio.currentTime * 1000));
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const stopProgressLoop = useCallback(() => {
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
  }, []);

  const handlePlayPause = useCallback(async () => {
    let url = audioUrl ?? urlRef.current;
    if (!url) {
      url = await fetchAndLoad();
      if (!url) return;
    }

    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlaying(false);
        setProgress(0);
        setCurrentMs(0);
        stopProgressLoop();
      };
      audio.onpause = () => { setPlaying(false); stopProgressLoop(); };
      audio.onplay = () => { setPlaying(true); startProgressLoop(); };
    } else if (audio.src !== url) {
      audio.src = url;
    }

    if (audio.paused) {
      try { await audio.play(); } catch { setError("не удалось воспроизвести"); }
    } else {
      audio.pause();
    }
  }, [audioUrl, fetchAndLoad, startProgressLoop, stopProgressLoop]);

  const handleSeek = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio);
    setCurrentMs(Math.floor(ratio * audio.duration * 1000));
  }, []);

  const displayMs = playing ? currentMs : durationMs;

  return (
    <div className="bubble__voice">
      <button
        type="button"
        className="bubble__voice-play"
        aria-label={playing ? "Пауза" : "Воспроизвести"}
        onClick={() => void handlePlayPause()}
        disabled={loading}
      >
        {loading ? (
          <span className="bubble__voice-loading" />
        ) : (
          <span
            aria-hidden="true"
            className="bubble__voice-play-icon"
            style={{
              maskImage: `url(/emoji/${playing ? "pause" : "play"}.svg)`,
              WebkitMaskImage: `url(/emoji/${playing ? "pause" : "play"}.svg)`,
            }}
          />
        )}
      </button>

      <div className="bubble__voice-waveform-wrap">
        <svg
          className="bubble__voice-waveform"
          viewBox={`0 0 ${BAR_COUNT * 4 - 1} 32`}
          preserveAspectRatio="none"
          aria-hidden="true"
          onClick={handleSeek}
          style={{ cursor: audioUrl ? "pointer" : "default" }}
        >
          {waveform.map((amp, i) => {
            const barH = Math.max(3, Math.round(amp * 28));
            const y = (32 - barH) / 2;
            const played = progress > 0 && i / BAR_COUNT < progress;
            return (
              <rect
                key={i}
                x={i * 4}
                y={y}
                width={3}
                height={barH}
                rx={1}
                className={played ? "bubble__voice-bar bubble__voice-bar--played" : "bubble__voice-bar"}
              />
            );
          })}
        </svg>
        {error ? (
          <span className="bubble__voice-err">{error}</span>
        ) : (
          <span className="bubble__voice-dur">{formatDuration(displayMs)}</span>
        )}
      </div>
    </div>
  );
}
